import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import { getUserEmail } from '@/utils/storage';
import { ApiEnvelope, Paginated } from '@/types/package';
import {
  GetChargesParams,
  GetCollectionsParams,
  GetRentalsParams,
  EMPTY_CHARGE_SUMMARY,
  RentalChargePage,
  RentalCollection,
  RentalAction,
  RentalActionBody,
  RentalDetail,
  RentalDocuments,
  RentalListItem,
  RentalPaginated,
  RetryRefundResponse,
  SimulatePaymentResponse,
  UpdateRentalDatesPayload,
  BulkCreateWaiversBody,
  BulkCreateWaiversResult,
  GetPreOrdersParams,
  GetRentalStatsParams,
  GetReturnRequestsParams,
  RentalStatsWithArrears,
  RentalDashboardQueues,
  RentalDashboardSummary,
  GetRentalDashboardQueuesParams,
  GetRentalDashboardSummaryParams,
  ReassignCandidates,
  MgmtPreOrder,
  GetPaidCollectionsParams,
  PaidCollectionsPage,
  EMPTY_PAID_COLLECTION_SUMMARY,
  MgmtReturnRequest,
  PauseBillingBody,
  PauseBillingResult,
  SettlementApprovalResult,
} from '@/types/rental';

/** Per-request options the dashboard endpoints accept. */
export interface RequestOptions {
  signal?: AbortSignal;
}

const MGMT_BASE = '/rent/mgmt/v1';
const BASE = `${MGMT_BASE}/rental`;

/** Dash Rent nests the payload under the `{ status, message, data }` envelope. */
const unwrap = <T>(payload: ApiEnvelope<T>): T => payload?.data as T;

/** See the same helper in `packages.ts` — uppercase `ID` PK casing. */
const withId = <T extends { id?: string; ID?: string }>(row: T): T =>
  row && !row.id && row.ID ? { ...row, id: row.ID } : row;

/** Fails fast on a missing or literal "undefined" path param. */
const requireId = (id: string | undefined, label = 'Rental'): string => {
  if (!id || id === 'undefined' || id === 'null') {
    throw new Error(`${label} id is missing from the URL`);
  }
  return id;
};

/**
 * The three multi-value filters go over the wire comma-joined, as in
 * `getRentalCollections` — the controller splits each back into a list, so one
 * request can cover several values at once.
 */
export const getRentals = async (
  params: GetRentalsParams = {}
): Promise<RentalPaginated<RentalListItem>> => {
  try {
    const response = await Axios.get<
      ApiEnvelope<RentalPaginated<RentalListItem>>
    >(BASE, {
      params: {
        ...params,
        status: params.status?.join(','),
        paymentStatus: params.paymentStatus?.join(','),
        escalationStatus: params.escalationStatus?.join(','),
      },
    });
    const page = unwrap(response.data);
    return { ...page, items: (page?.items ?? []).map(withId) };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Row count per Rent Activity tab under the current non-tab filters.
 *
 * Takes no `status`/`isOverdue`/`isOther`: those are the tabs themselves, and
 * the counts are what tells you how full each tab is before you open it. The
 * two multi-value filters go over the wire comma-joined, as in
 * `getRentalCollections`.
 */
export const getRentalStats = async (
  params: GetRentalStatsParams = {},
  options: RequestOptions = {}
): Promise<RentalStatsWithArrears> => {
  try {
    const response = await Axios.get<ApiEnvelope<RentalStatsWithArrears>>(
      `${BASE}/stats`,
      {
        signal: options.signal,
        params: {
          ...params,
          paymentStatus: params.paymentStatus?.join(','),
          escalationStatus: params.escalationStatus?.join(','),
        },
      }
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const getRentalById = async (id: string): Promise<RentalDetail> => {
  try {
    const response = await Axios.get<ApiEnvelope<RentalDetail>>(
      `${BASE}/${requireId(id)}`
    );
    const detail = withId(unwrap(response.data));
    // `withId` only normalises the top-level row — the backend also sends
    // uppercase `ID` on each nested deduction row (same convention as its
    // own `waivers`/`returnRequests` arrays).
    return {
      ...detail,
      deductions: detail.deductions?.map(withId),
    };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Just the rental's documents block — what the detail polls while a checklist
 * correction's reissue renders, instead of the whole (much heavier) detail.
 */
export const getRentalDocuments = async (
  id: string
): Promise<RentalDocuments> => {
  try {
    const response = await Axios.get<ApiEnvelope<RentalDocuments>>(
      `${BASE}/${requireId(id)}/documents`
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * A blob `responseType` applies to the ERROR body too, so a 403's `{ message }`
 * arrives as a Blob and `getBackendErrorMessage` cannot see past it. Read it
 * back as text — the eligibility reason IS the answer this endpoint's 403
 * carries, and it is the only thing that tells ops why the sheet is refused.
 */
const readBlobErrorMessage = async (error: unknown): Promise<string> => {
  const body = (error as { response?: { data?: unknown } })?.response?.data;
  if (body instanceof Blob) {
    try {
      const parsed = JSON.parse(await body.text());
      const message = parsed?.errors?.[0]?.message ?? parsed?.message;
      if (typeof message === 'string' && message) return message;
    } catch {
      // Not JSON (a proxy error page, say) — fall through to the axios message.
    }
  }
  return getBackendErrorMessage(error);
};

/**
 * "Surat Pernyataan dan Kesanggupan Penjamin" — the sheet the penyewa takes to
 * their penjamin. PDF BYTES, not a URL: the form is rendered per request and
 * never stored, which is why it has no entry in `detail.documents`.
 *
 * 403 when the account named on the sheet is not rent-eligible (KYC), and that
 * reason must reach the screen — see `readBlobErrorMessage`.
 */
export const downloadRentalGuarantorLetter = async (
  id: string
): Promise<Blob> => {
  try {
    const response = await Axios.get(
      `${BASE}/${requireId(id)}/guarantor-letter`,
      { responseType: 'blob' }
    );
    return response.data as Blob;
  } catch (error: unknown) {
    throw new Error(await readBlobErrorMessage(error));
  }
};

/**
 * Payment history for one rental, paginated. Replaces the `payments[]` array
 * that used to ride along on the detail response.
 *
 * `status` and `purpose` go over the wire comma-joined, and the date range
 * filters `createdAt` rather than `paidAt` — see GetCollectionsParams.
 */
export const getRentalCollections = async (
  id: string,
  params: GetCollectionsParams = {}
): Promise<RentalPaginated<RentalCollection>> => {
  try {
    const response = await Axios.get<
      ApiEnvelope<RentalPaginated<RentalCollection>>
    >(`${BASE}/${requireId(id)}/collections`, {
      params: {
        ...params,
        status: params.status?.join(','),
        purpose: params.purpose?.join(','),
      },
    });
    const page = unwrap(response.data);
    return { ...page, items: (page?.items ?? []).map(withId) };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Settled payments across every rental, windowed on `paidAt` in WIB — the
 * dashboard's "Pembayaran Hari Ini" feed. Unlike `getRentalCollections` this
 * one is paidAt-based and PAID-only: the question is what money landed, so a
 * PENDING or FAILED row is not an answer to it.
 *
 * The summary is defaulted so a page without one renders zeros rather than
 * blanking the header.
 */
export const getPaidCollections = async (
  params: GetPaidCollectionsParams = {}
): Promise<PaidCollectionsPage> => {
  try {
    const response = await Axios.get<ApiEnvelope<PaidCollectionsPage>>(
      `${MGMT_BASE}/collections`,
      {
        params: {
          ...params,
          // Comma-joined, as in `getRentalCollections`.
          purpose: params.purpose?.length
            ? params.purpose.join(',')
            : undefined,
        },
      }
    );
    const page = unwrap(response.data);
    return {
      ...page,
      items: (page?.items ?? []).map(withId),
      summary: page?.summary ?? EMPTY_PAID_COLLECTION_SUMMARY,
    };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * The daily charge ledger for one rental, paginated.
 *
 * `status` and `type` go over the wire comma-joined, as in
 * `getRentalCollections`, and the date range filters `chargeDate` — the day
 * being billed, which is the axis this screen reads on.
 *
 * The summary is defaulted rather than left undefined: it covers the whole
 * rental and the header renders it unconditionally, so a page without one
 * should show zeros instead of blanking the row.
 */
export const getRentalCharges = async (
  id: string,
  params: GetChargesParams = {}
): Promise<RentalChargePage> => {
  try {
    const response = await Axios.get<ApiEnvelope<RentalChargePage>>(
      `${BASE}/${requireId(id)}/charges`,
      {
        params: {
          ...params,
          status: params.status?.join(','),
          type: params.type?.join(','),
        },
      }
    );
    const page = unwrap(response.data);
    return {
      ...page,
      items: (page?.items ?? []).map(withId),
      summary: page?.summary ?? EMPTY_CHARGE_SUMMARY,
    };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Settles one existing PENDING collection without paying — dev/QA only.
 *
 * `collections` sits beside `rental` on the mgmt surface, not under it, so this
 * does not go through BASE. The endpoint never mints a charge or a QR: those
 * come from the driver's `POST /rental` (DP) and `POST /rental/:id/pay`
 * (top-up), so it 404s when there is nothing pending instead of creating one.
 *
 * Refused server-side on production (403 `SIMULATE_NOT_ALLOWED_IN_PRODUCTION`);
 * 409 when the collection exists but has already left PENDING.
 */
export const simulateCollectionPayment = async (
  collectionId: string
): Promise<SimulatePaymentResponse> => {
  try {
    const response = await Axios.post<ApiEnvelope<SimulatePaymentResponse>>(
      `${MGMT_BASE}/collections/${requireId(collectionId, 'Collection')}/simulate-payment`
    );
    return unwrap(response.data) ?? {};
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Re-instruct a refund payout — not dev/QA, unlike the simulate above it.
 * NOT_INSTRUCTED and FAILED only: PENDING, COMPLETED and a superseded attempt
 * 409, and a gateway refusal comes back 422 with the destination error.
 */
export const retryRefundPayout = async (
  payoutId: string
): Promise<RetryRefundResponse> => {
  try {
    const response = await Axios.post<ApiEnvelope<RetryRefundResponse>>(
      `${MGMT_BASE}/refund-payouts/${requireId(payoutId, 'Refund payout')}/retry`,
      { updatedBy: getUserEmail() }
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * One action per call — see RentalAction.
 *
 * `updatedBy` is required by the API on every action and is stamped here
 * rather than at each call site, the same way `packages.ts` stamps
 * `createdBy`/`updatedBy`. The contract documents only `action` and `payload`,
 * but `UpdateMgmtRentalRequestDto` marks the actor `@IsNotEmpty()`, so a body
 * without it is a 400 for *all* eight actions.
 */
export const patchRental = async (
  id: string,
  body: RentalActionBody
): Promise<RentalDetail> => {
  try {
    const response = await Axios.patch<ApiEnvelope<RentalDetail>>(
      `${BASE}/${requireId(id)}`,
      { ...body, updatedBy: getUserEmail() }
    );
    return withId(unwrap(response.data));
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Jeda Tagihan. `dryRun: true` is the modal's impact preview. The actor comes
 * from the token, never the body. Errors are rethrown raw (not wrapped) so the
 * caller can tell a 409 state conflict from a validation error.
 */
export const pauseRentalBilling = async (
  id: string,
  body: PauseBillingBody
): Promise<PauseBillingResult> => {
  const response = await Axios.post<ApiEnvelope<PauseBillingResult>>(
    `${BASE}/${requireId(id)}/billing-pause`,
    body
  );
  return unwrap(response.data);
};

/** Billing starts again today. 409 when the rental is not paused. */
type ResumeBillingResult = { rentalID: string; billedAgainFrom: string };

export const resumeRentalBilling = async (
  id: string
): Promise<ResumeBillingResult> => {
  const response = await Axios.post<ApiEnvelope<ResumeBillingResult>>(
    `${BASE}/${requireId(id)}/billing-pause/resume`
  );
  return unwrap(response.data);
};

export const holdEscalation = (id: string) =>
  patchRental(id, { action: RentalAction.HOLD_ESCALATION });

export const resumeEscalation = (id: string) =>
  patchRental(id, { action: RentalAction.RESUME_ESCALATION });

/**
 * Ops override of the pickup slot or return date. Send only the fields that
 * change; the backend re-validates against the hub's active days.
 */
export const updateRentalDates = (
  id: string,
  payload: UpdateRentalDatesPayload
) => patchRental(id, { action: RentalAction.UPDATE_DATES, payload });

/**
 * Pre-handover only. Refunds the DP in full gross-of-MDR — the disbursement
 * rail always deducts its fixed fee (M4/B5 revised).
 */
export const cancelRental = (id: string) =>
  patchRental(id, { action: RentalAction.CANCEL });

/**
 * A rent payment the driver has not paid yet: PENDING PREPAY only (a DP is
 * withdrawn by cancelling the booking). Its voucher goes back to the driver.
 */
export const cancelRentalPayment = (id: string, collectionID: string) =>
  patchRental(id, {
    action: RentalAction.CANCEL_PAYMENT,
    payload: { collectionID },
  });

export const forceEarlyReturn = (
  id: string,
  payload: { reason: string; settlementNotes?: string }
) => patchRental(id, { action: RentalAction.FORCE_EARLY_RETURN, payload });

/** MAKER side of the maker-checker settlement (TRD O4). */
export const settleRental = (
  id: string,
  payload: {
    terminationReason: string;
    depositRefundAmount: number;
    settlementNotes?: string;
  }
) => patchRental(id, { action: RentalAction.SETTLE, payload });

/** Non-connected fleet: ops executed the shutdown in the field (TRD L3). */
export const markShutdownDone = (id: string) =>
  patchRental(id, { action: RentalAction.MARK_SHUTDOWN_DONE });

/** `overrideReason` is required by the API for a unit with `requiresOverride`. */
export const reassignVehicle = (
  id: string,
  vehicleID: string,
  overrideReason?: string
) =>
  patchRental(id, {
    action: RentalAction.REASSIGN_VEHICLE,
    payload: { vehicleID, overrideReason },
  });

/**
 * The units `reassignVehicle` would accept for this rental — pre-filtered by
 * the rental's own package rules, so the picker never offers a unit the swap
 * then rejects. 409 when the rental is not reassignable (already picked up).
 */
export const getReassignCandidates = async (
  id: string
): Promise<ReassignCandidates> => {
  try {
    const response = await Axios.get<ApiEnvelope<ReassignCandidates>>(
      `${BASE}/${requireId(id)}/reassign-candidates`
    );
    const data = unwrap(response.data);
    return { items: data?.items ?? [], packageStatus: data?.packageStatus };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * The rent dashboard's money aggregates: month-to-date Collection Rate and
 * the fleet-wide arrears total. Pair with `getRentalStats` for the counts.
 */
export const getRentalDashboardSummary = async (
  params: GetRentalDashboardSummaryParams = {},
  options: RequestOptions = {}
): Promise<RentalDashboardSummary> => {
  try {
    const response = await Axios.get<ApiEnvelope<RentalDashboardSummary>>(
      `${BASE}/dashboard-summary`,
      { params, signal: options.signal }
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Per-queue stats for the dashboard's action tabs — a today-snapshot, so it is
 * fetched once and NOT re-fetched when the collection cards' date window moves.
 * `date` overrides the day the scheduled queues are counted for.
 */
export const getRentalDashboardQueues = async (
  params: GetRentalDashboardQueuesParams = {},
  options: RequestOptions = {}
): Promise<RentalDashboardQueues> => {
  try {
    const response = await Axios.get<ApiEnvelope<RentalDashboardQueues>>(
      `${BASE}/dashboard-queues`,
      { params, signal: options.signal }
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * CHECKER side of settlement (TRD O4).
 *
 * `approvedBy` is required by the API and undocumented in the contract, so it
 * is stamped here. It is also load-bearing rather than audit decoration: the
 * backend rejects an approver equal to the SETTLE maker, and that comparison
 * is made against this value.
 */
export const approveSettlement = async (
  rentalId: string,
  body: { approved: boolean; notes?: string }
): Promise<SettlementApprovalResult> => {
  try {
    const response = await Axios.post<ApiEnvelope<SettlementApprovalResult>>(
      `${MGMT_BASE}/settlements/${requireId(rentalId)}/approve`,
      { ...body, approvedBy: getUserEmail() }
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// ---------------------------------------------------------------------------
// Free days — waivers
// ---------------------------------------------------------------------------

/**
 * Grants one free day across a scope of packages and/or rentals.
 *
 * The tenure gate is applied per rental server-side, so the response splits
 * what it actually created from what it skipped as ineligible — a caller that
 * only counts `created` will under-report and look like a partial failure.
 *
 * `grantedBy` is required and undocumented, same as the other actor fields.
 */
export const bulkCreateWaivers = async (
  body: BulkCreateWaiversBody
): Promise<BulkCreateWaiversResult> => {
  try {
    const response = await Axios.post<ApiEnvelope<BulkCreateWaiversResult>>(
      `${MGMT_BASE}/waivers/bulk`,
      { ...body, grantedBy: getUserEmail() }
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** Only while the charge is still PENDING or absent (TRD H5). */
export const deleteWaiver = async (
  waiverId: string
): Promise<{ ID: string }> => {
  try {
    const response = await Axios.delete<ApiEnvelope<{ ID: string }>>(
      `${MGMT_BASE}/waivers/${requireId(waiverId, 'Waiver')}`
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// ---------------------------------------------------------------------------
// Queues
// ---------------------------------------------------------------------------

/**
 * Return queue. Paginated, though the contract shows only `date`/`status` —
 * the server defaults to `size` 50 here rather than the usual 10.
 */
export const getReturnRequests = async (
  params: GetReturnRequestsParams = {}
): Promise<Paginated<MgmtReturnRequest>> => {
  try {
    const response = await Axios.get<ApiEnvelope<Paginated<MgmtReturnRequest>>>(
      `${MGMT_BASE}/return-requests`,
      { params }
    );
    const page = unwrap(response.data);
    return { ...page, items: page?.items ?? [] };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * FIFO pre-order queue for one package. `packageID` is required — the endpoint
 * reads a queue per package, not a global list. Not paginated: `queuePosition`
 * is the row's index in the whole queue, so a page would make it meaningless.
 */
export const getPreOrders = async (
  params: GetPreOrdersParams
): Promise<{ items: MgmtPreOrder[] }> => {
  try {
    const response = await Axios.get<ApiEnvelope<{ items: MgmtPreOrder[] }>>(
      `${MGMT_BASE}/pre-orders`,
      { params }
    );
    const data = unwrap(response.data);
    return { items: data?.items ?? [] };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};
